import React, { useCallback, useEffect, useState } from "react";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getItem } from "../api/generics";
import OperationalDocumentsModal from "../components/operational-monitoring/OperationalDocumentsModal";
import OperationalLotsModal from "../components/operational-monitoring/OperationalLotsModal";

interface CountByLabel {
  label: string;
  count: number;
}

interface ErrorCountByType {
  label: string;
  error: number;
  recoverable: number;
  total: number;
}

interface OperationalStats {
  generatedAt: string;
  errorPeriod: {
    start: string;
    end: string;
    label: string;
  };
  processingLots: {
    total: number;
    byStatus: CountByLabel[];
  };
  uploadedDocuments: {
    total: number;
    byType: CountByLabel[];
  };
  errors: {
    total: number;
    permanent: number;
    recoverable: number;
    byType: ErrorCountByType[];
  };
}

const HorizontalCountChart: React.FC<{
  data: CountByLabel[];
  color: string;
  valueLabel: string;
}> = ({ data, color, valueLabel }) => (
  <ResponsiveContainer width="100%" height={240}>
    <BarChart
      data={data}
      layout="vertical"
      margin={{ top: 5, right: 25, left: 20, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
      <XAxis type="number" allowDecimals={false} />
      <YAxis dataKey="label" type="category" width={145} />
      <Tooltip formatter={(value) => [value, valueLabel]} />
      <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const OperationalDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<OperationalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showLots, setShowLots] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getItem<OperationalStats>(
        "/admin-stats/operations/",
      );
      const data = response.data ?? (response as unknown as OperationalStats);
      if (!data?.processingLots || !data?.uploadedDocuments || !data?.errors) {
        setStats(null);
        setError(response.detail ?? "No se pudieron cargar los indicadores.");
        return;
      }
      setStats(data);
    } catch {
      setStats(null);
      setError("No se pudieron cargar los indicadores operativos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <div>
      <CCard>
        <CCardBody className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mb-1">Monitoreo SIFEN</h1>
            <div className="text-muted">
              Ambiente de producción: lotes en procesamiento, documentos sin aprobación y errores operativos.
            </div>
          </div>
          <CButton color="primary" onClick={loadStats} disabled={loading}>
            {loading ? "Actualizando…" : "Actualizar"}
          </CButton>
        </CCardBody>
      </CCard>

      {error && <CAlert color="danger">{error}</CAlert>}

      <div style={{ position: "relative", minHeight: 260 }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--theme-loading-overlay)",
            }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {stats && (
          <>
            <CRow>
              <CCol sm="12" lg="4">
                <CCard className="text-dark bg-warning">
                  <CCardBody>
                    <div className="h2 mb-0">{stats.processingLots.total}</div>
                    <div>Lotes esperando procesamiento</div>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol sm="12" lg="4">
                <CCard className="text-white bg-info">
                  <CCardBody>
                    <div className="h2 mb-0">
                      {stats.uploadedDocuments.total}
                    </div>
                    <div>Documentos subidos sin aprobación</div>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol sm="12" lg="4">
                <CCard className="text-white bg-danger">
                  <CCardBody>
                    <div className="h2 mb-0">{stats.errors.total}</div>
                    <div>Documentos con error este mes</div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            <div className="d-flex justify-content-end mb-3">
              <CButton
                color="warning"
                className="mr-2"
                onClick={() => setShowLots(true)}
              >
                Ver lotes
              </CButton>
              <CButton color="info" onClick={() => setShowDocuments(true)}>
                Ver documentos y errores
              </CButton>
            </div>

            <CRow>
              <CCol lg="6">
                <CCard>
                  <CCardHeader>
                    <strong>Lotes esperando procesamiento</strong>
                  </CCardHeader>
                  <CCardBody>
                    <HorizontalCountChart
                      data={stats.processingLots.byStatus}
                      color="#f9b115"
                      valueLabel="Lotes"
                    />
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol lg="6">
                <CCard>
                  <CCardHeader>
                    <strong>Subidos, todavía no aprobados</strong>
                  </CCardHeader>
                  <CCardBody>
                    <HorizontalCountChart
                      data={stats.uploadedDocuments.byType}
                      color="#39f"
                      valueLabel="Documentos"
                    />
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            <CCard>
              <CCardHeader className="d-flex justify-content-between">
                <strong>Errores por tipo de documento</strong>
                <span className="text-muted">Mes actual</span>
              </CCardHeader>
              <CCardBody>
                <CRow className="mb-3">
                  <CCol sm="6">
                    <strong>{stats.errors.permanent}</strong> errores
                  </CCol>
                  <CCol sm="6">
                    <strong>{stats.errors.recoverable}</strong> errores recuperables
                  </CCol>
                </CRow>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={stats.errors.byType}
                    margin={{ top: 10, right: 20, left: 0, bottom: 15 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="error"
                      name="Error"
                      stackId="errors"
                      fill="#e55353"
                    />
                    <Bar
                      dataKey="recoverable"
                      name="Error recuperable"
                      stackId="errors"
                      fill="#f9b115"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CCardBody>
            </CCard>

            <div className="text-muted text-right small mb-3">
              Actualizado: {new Date(stats.generatedAt).toLocaleString()}
            </div>
          </>
        )}
      </div>

      <OperationalDocumentsModal
        show={showDocuments}
        onClose={() => setShowDocuments(false)}
        onChanged={loadStats}
      />
      <OperationalLotsModal
        show={showLots}
        onClose={() => setShowLots(false)}
      />
    </div>
  );
};

export default OperationalDashboardPage;
